import { TranslateService } from '@ngx-translate/core';

import { Injectable, Type } from '@angular/core';
import {
  MatLegacyDialog as MatDialog,
  MatLegacyDialogRef as MatDialogRef,
} from '@angular/material/legacy-dialog';

import { Observable, catchError, filter, map, of } from 'rxjs';

import { MwNotificationService } from '@core/services';

import {
  DialogColors,
  DialogConfigTyped,
  DialogTranslations,
  DialogType,
  DialogTypes,
} from '../config';
import { MwDialogComponent } from '../dialog.component';
import { DialogResult } from '../models/dialog.result';
import { DialogConfigService } from './dialog-config.service';

@Injectable({
  providedIn: 'root',
})
export class MwDialogService {
  constructor(
    private readonly dialog: MatDialog,
    private readonly notificationService: MwNotificationService,
    private readonly translateService: TranslateService
  ) {}

  open<T>(
    config: DialogConfigTyped<T>
  ): MatDialogRef<MwDialogComponent<T>, any> {
    const dialogConfig = DialogConfigService.buildMatDialogConfig(config);

    const dialogRef = this.dialog.open<
      MwDialogComponent<T>,
      DialogConfigTyped<T>,
      any
    >(MwDialogComponent, dialogConfig);

    const originalAfterClose = dialogRef.afterClosed();

    dialogRef.afterClosed = () =>
      originalAfterClose.pipe(
        map((result) => result || new DialogResult(DialogResult.Cancel))
      );

    return dialogRef;
  }

  confirmDelete(config?: {
    text?: string;
    title?: string;
    yesButton?: string;
    noButton?: string;
    color?: DialogColors;
    dialogType?: DialogType;
    skipResultMessage?: boolean;
    itemName?: string | ((result?: DialogResult) => string);
    deleteConfirmed?: () => Observable<DialogResult>;
    successMessage?: (result: DialogResult) => string;
    failMessage?: (result: DialogResult) => string;
  }): Observable<boolean> {
    const translations =
      DialogConfigService.buildConfirmDeleteDialogTranslations(config);
    var dialogRef = this.open({
      dialogType: config?.dialogType || DialogTypes.Small,
      colors: config?.color || DialogColors.Warn,
      translations,
      showInProgressAfterConfirm: !!config?.deleteConfirmed,
      autoClose: false,
    });

    const deleteConfirmedFn = (): Observable<DialogResult> =>
      config?.deleteConfirmed
        ? config.deleteConfirmed()
        : of(new DialogResult(DialogResult.Success));

    dialogRef.componentInstance.onConfirmClicked.subscribe(() => {
      this.handleDeleteDialogResult(dialogRef.componentInstance, {
        result$: deleteConfirmedFn(),
        successMessage: config?.successMessage,
        failMessage: config?.failMessage,
        skipResultMessage: config?.skipResultMessage,
        itemName: config?.itemName,
      });
    });

    return dialogRef.afterClosed().pipe(
      map(
        (result: DialogResult) => result?.resultCode === DialogResult.Success
      ),
      filter((result) => !!result)
    );
  }

  confirm(config?: {
    translations?: DialogTranslations;
    showTitle?: boolean;
    showMsg?: boolean;
    singleButton?: boolean;
    colors?: DialogColors;
    dialogType?: DialogType;
  }): { afterClosed: () => Observable<boolean> } {
    const translations =
      DialogConfigService.buildConfirmDialogTranslations(config);
    const dialogRef = this.open({
      dialogType: config?.dialogType || DialogTypes.XSmall,
      colors: config?.colors || DialogColors.Accent,
      autoClose: true,
      showAdditionalButton: !!translations.additionalButton,
      translations,
    });

    // Backward compatibility result
    return {
      afterClosed: () =>
        dialogRef
          .afterClosed()
          .pipe(map((result) => result === DialogResult.Confirm)),
    };
  }

  modify<T>(
    config: {
      content: Type<T>;
      translations?: DialogTranslations;
      itemName?: string | ((result?: DialogResult) => string);
      successMessage?: (result: DialogResult) => string;
      failMessage?: (result: DialogResult) => string;
      size?: DialogType;
      isEditDialog?: boolean;
    },
    opts?: DialogConfigTyped<T>
  ): MatDialogRef<MwDialogComponent<T>, DialogResult> {
    const translations =
      DialogConfigService.buildModifyDialogTranslations(config);

    const dialogRef = this.open({
      translations,
      dialogType: config.size,
      showInProgressAfterConfirm: true,
      autoClose: false,
      isFlexHeight: true,
      customContentComponent: config.content,
      ...(opts ?? {}),
    });

    this.handleModifyDialogResult(dialogRef.componentInstance, config);

    return dialogRef;
  }

  private handleModifyDialogResult<T>(
    dialogComponentInstance: MwDialogComponent<T>,
    config: {
      successMessage?: (result: DialogResult) => string;
      failMessage?: (result: DialogResult) => string;
      isEditDialog?: boolean;
      itemName?: string | ((result?: DialogResult) => string);
    }
  ): void {
    dialogComponentInstance.dialogResult$
      .pipe(
        catchError((err): any => {
          dialogComponentInstance.resetProgressState();
          throw err;
        })
      )
      .subscribe((result: DialogResult) => {
        let defaultSuccessMessageTranslation = config.itemName
          ? 'CORE.COMPONENT.DIALOG.add_dialog_success_message_with_param'
          : 'CORE.COMPONENT.DIALOG.add_dialog_success_message';

        let defaultFailMessageTranslation = config.itemName
          ? 'CORE.COMPONENT.DIALOG.add_dialog_fail_message_with_param'
          : 'CORE.COMPONENT.DIALOG.add_dialog_fail_message';

        if (config.isEditDialog) {
          defaultSuccessMessageTranslation = config.itemName
            ? 'CORE.COMPONENT.DIALOG.edit_dialog_success_message_with_param'
            : 'CORE.COMPONENT.DIALOG.edit_dialog_success_message';

          defaultFailMessageTranslation = config.itemName
            ? 'CORE.COMPONENT.DIALOG.edit_dialog_fail_message_with_param'
            : 'CORE.COMPONENT.DIALOG.edit_dialog_fail_message';
        }

        const interpolateParams = {
          item:
            typeof config.itemName === 'string'
              ? config.itemName
              : typeof config.itemName === 'function'
              ? config.itemName(result)
              : null,
          resultMessage: result.resultMessage,
        };

        if (result.resultCode === DialogResult.Success) {
          this.showMessage(
            result,
            config.successMessage,
            defaultSuccessMessageTranslation,
            interpolateParams
          );

          dialogComponentInstance.close(result);
        } else {
          this.showMessage(
            result,
            config.failMessage,
            defaultFailMessageTranslation,
            interpolateParams
          );
          dialogComponentInstance.resetProgressState();
        }
      });
  }

  private handleDeleteDialogResult<T>(
    dialog: MwDialogComponent<T>,
    config: {
      result$: Observable<DialogResult>;
      successMessage?: (result: DialogResult) => string;
      failMessage?: (result: DialogResult) => string;
      skipResultMessage?: boolean;
      itemName?: string | ((result?: DialogResult) => string);
    }
  ): void {
    config.result$
      .pipe(
        catchError((err): any => {
          dialog.resetProgressState();
          throw err;
        })
      )
      .subscribe((result: DialogResult) => {
        if (result.resultCode === DialogResult.Success) {
          dialog.close(result);
        } else {
          dialog.resetProgressState();
        }

        if (config.skipResultMessage) {
          return;
        }

        const defaultSuccessMessageTranslation = config.itemName
          ? 'CORE.COMPONENT.DIALOG.delete_dialog_success_message_with_param'
          : 'CORE.COMPONENT.DIALOG.delete_dialog_success_message';

        const defaultFailMessageTranslation = config.itemName
          ? 'CORE.COMPONENT.DIALOG.delete_dialog_fail_message_with_param'
          : 'CORE.COMPONENT.DIALOG.delete_dialog_fail_message';

        const interpolateParams = {
          item:
            typeof config.itemName === 'string'
              ? config.itemName
              : typeof config.itemName === 'function'
              ? config.itemName(result)
              : null,
          resultMessage: result.resultMessage,
        };

        if (result.resultCode === DialogResult.Success) {
          this.showMessage(
            result,
            config.successMessage,
            defaultSuccessMessageTranslation,
            interpolateParams
          );

          dialog.close(result);
        } else {
          this.showMessage(
            result,
            config.failMessage,
            defaultFailMessageTranslation,
            interpolateParams
          );
          dialog.resetProgressState();
        }
      });
  }

  private showMessage(
    result: DialogResult,
    messageFormatter: (result: DialogResult) => string,
    defaultMessage: string,
    interpolateParams?: Object
  ): void {
    let message = messageFormatter
      ? messageFormatter(result)
      : this.translateService.instant(defaultMessage, interpolateParams);

    message = message.charAt(0).toUpperCase() + message.slice(1);

    if (result.resultCode === DialogResult.Success) {
      this.notificationService.success(message);
    } else {
      this.notificationService.error(message);
    }
  }
}
